import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  AssignmentReturn as ReturnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material/styles';
import * as S from './OrderDetailsModal.styles';
import { formatNotesMessage } from '../../../../utils/productHistoryNotes.utils';
import {
  formatReturnHistoryDetail,
  parseReturnHistoryNotes,
} from '../../../../utils/returnHistoryNotes.utils';
import { usePermission } from '../../../../hooks/usePermission';
import { ORDERS_PERMISSIONS } from '../../../../constants/permissions.constants';
import {
  buildProductHistoryTimeline,
  getExchangeSummaryLabel,
  getReturnSummaryLabel,
  HistoryTimelineEntry,
  ProductHistoryItem,
  toMoney,
} from './productOrderHistory.utils';

interface ProductHistorySectionProps {
  orderId: number;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
  /** Si se provee, no se consulta la API y se usa este historial (p. ej. Ventas por Pieza). */
  embeddedHistory?: ProductHistoryItem[];
  /** Título de sección personalizado */
  sectionTitle?: string;
  /** Omitir comprobación de permiso view_row (menú con un solo permiso). */
  skipPermissionCheck?: boolean;
}

const getActionLabel = (actionType: string): string => {
  const labels: Record<string, string> = {
    CREATED: 'Creado',
    EXCHANGED_OUT: 'Sale de la orden',
    EXCHANGED_IN: 'Ingresa a la orden',
    RETURNED: 'Devuelto',
    REFUNDED: 'Reembolsado',
  };
  return labels[actionType] || actionType;
};

const getActionChipStyles = (actionType: string, theme: Theme) => {
  const dark = theme.palette.mode === 'dark';

  const styles: Record<string, { bgcolor: string; color: string }> = {
    CREATED: {
      bgcolor: alpha(theme.palette.success.main, dark ? 0.22 : 0.12),
      color: dark ? theme.palette.success.light : theme.palette.success.dark,
    },
    EXCHANGED_OUT: {
      bgcolor: alpha(theme.palette.grey[500], dark ? 0.32 : 0.14),
      color: dark ? theme.palette.grey[100] : theme.palette.grey[800],
    },
    EXCHANGED_IN: {
      bgcolor: alpha(theme.palette.info.main, dark ? 0.22 : 0.12),
      color: dark ? theme.palette.info.light : theme.palette.info.dark,
    },
    RETURNED: {
      bgcolor: alpha(theme.palette.error.main, dark ? 0.22 : 0.12),
      color: dark ? theme.palette.error.light : theme.palette.error.dark,
    },
    REFUNDED: {
      bgcolor: alpha(theme.palette.error.main, dark ? 0.22 : 0.12),
      color: dark ? theme.palette.error.light : theme.palette.error.dark,
    },
    EXCHANGE: {
      bgcolor: alpha(theme.palette.warning.main, dark ? 0.22 : 0.12),
      color: dark ? theme.palette.warning.light : theme.palette.warning.dark,
    },
    EXCHANGE_REJECTED: {
      bgcolor: alpha(theme.palette.grey[500], dark ? 0.32 : 0.14),
      color: dark ? theme.palette.grey[100] : theme.palette.grey[800],
    },
  };

  return (
    styles[actionType] ?? {
      bgcolor: alpha(theme.palette.grey[500], dark ? 0.32 : 0.14),
      color: dark ? theme.palette.grey[100] : theme.palette.grey[800],
    }
  );
};

const ExchangeBlockTitle: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}> = ({ icon, label, count, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
    <Box sx={{ display: 'flex', color, lineHeight: 0 }}>{icon}</Box>
    <Typography variant="caption" sx={{ fontWeight: 700, color }}>
      {label} ({count})
    </Typography>
  </Box>
);

const ExchangeAmountSummary: React.FC<{
  label: string;
  amount: string;
  icon: React.ReactNode;
  color: 'error.main' | 'success.main';
}> = ({ label, amount, icon, color }) => (
  <Box sx={{ textAlign: 'right', minWidth: 72 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.25,
        color,
        mb: 0.25,
      }}
    >
      {icon}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={700} color={color}>
      {amount}
    </Typography>
  </Box>
);

const rowShellSx = (isLast: boolean) => ({
  p: 2,
  borderBottom: isLast ? 0 : 1,
  borderColor: 'divider',
});

const ReturnProductLine: React.FC<{
  item: ProductHistoryItem;
  formatCurrency: (amount: number) => string;
}> = ({ item, formatCurrency }) => {
  const itemMeta = parseReturnHistoryNotes(item.notes);
  const refundDisplay =
    itemMeta.refundAmount != null ? itemMeta.refundAmount : toMoney(item.originalPrice);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.04)
            : alpha(theme.palette.common.black, 0.02),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {item.product.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Código: {item.warehouseInventoryProduct?.productCodeUnique || 'N/A'}
        </Typography>
        {itemMeta.itemCondition ? (
          <Typography variant="caption" color="text.secondary" display="block">
            Condición: {itemMeta.itemCondition}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" fontWeight={600} color="error.main" sx={{ flexShrink: 0 }}>
        {formatCurrency(refundDisplay)}
      </Typography>
    </Box>
  );
};

const ProductLine: React.FC<{
  item: ProductHistoryItem;
  formatCurrency: (amount: number) => string;
  priceColor?: string;
}> = ({ item, formatCurrency, priceColor = 'text.primary' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 0.75,
      px: 1,
      borderRadius: 1,
      bgcolor: (theme) =>
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.04)
          : alpha(theme.palette.common.black, 0.02),
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>
        {item.product.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Código: {item.warehouseInventoryProduct?.productCodeUnique || 'N/A'}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={700} color={priceColor} sx={{ flexShrink: 0 }}>
      {formatCurrency(toMoney(item.finalPrice))}
    </Typography>
  </Box>
);

const SingleHistoryRow: React.FC<{
  item: ProductHistoryItem;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
  isLast: boolean;
}> = ({ item, formatCurrency, formatDate, isLast }) => {
  const isReturn = item.actionType === 'RETURNED' || item.actionType === 'REFUNDED';
  const returnDetail = isReturn
    ? formatReturnHistoryDetail(parseReturnHistoryNotes(item.notes))
    : undefined;
  const notesMessage = isReturn ? returnDetail : formatNotesMessage(item.notes);

  return (
    <Box sx={rowShellSx(isLast)}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={getActionLabel(item.actionType)}
              size="small"
              sx={(theme) => ({
                fontWeight: 600,
                ...getActionChipStyles(item.actionType, theme),
                border: 'none',
              })}
            />
            <Typography variant="body2" fontWeight={700}>
              {item.product.name}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Código: {item.warehouseInventoryProduct?.productCodeUnique || 'N/A'}
          </Typography>
          {notesMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
            >
              {notesMessage}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}>
          {!isReturn && (
            <Typography variant="body2" fontWeight={700} color="primary.main">
              {formatCurrency(toMoney(item.finalPrice))}
            </Typography>
          )}
          {isReturn && (
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {formatCurrency(toMoney(item.originalPrice))}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDate(item.createdAt)}
          </Typography>
          {item.processedBy?.username && (
            <Typography variant="caption" color="text.secondary" display="block">
              {item.processedBy.username}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ReturnHistoryRow: React.FC<{
  entry: Extract<HistoryTimelineEntry, { kind: 'return' }>;
  expanded: boolean;
  onToggle: () => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
  isLast: boolean;
}> = ({ entry, expanded, onToggle, formatCurrency, formatDate, isLast }) => {
  const refundTotal = entry.orderRefundAmount;
  const summary = getReturnSummaryLabel(
    entry.items.length,
    refundTotal,
    formatCurrency,
    entry.isPartial,
  );
  const notesMessage = formatReturnHistoryDetail(entry.returnMeta);

  return (
    <Box sx={rowShellSx(isLast)}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<ReturnIcon sx={{ fontSize: '14px !important' }} />}
              label={entry.isPartial ? 'Devolución parcial' : 'Devolución'}
              size="small"
              sx={(theme) => ({
                fontWeight: 600,
                ...getActionChipStyles('RETURNED', theme),
                border: 'none',
                '& .MuiChip-icon': { color: 'inherit' },
              })}
            />
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDate(entry.createdAt)} · {entry.processedByUsername}
          </Typography>
          {!expanded && notesMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
              noWrap
            >
              {notesMessage}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
          <Box sx={{ textAlign: 'right', mr: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Reembolso
            </Typography>
            <Typography variant="body2" fontWeight={700} color="error.main">
              {formatCurrency(refundTotal)}
            </Typography>
          </Box>
          <Tooltip title={expanded ? 'Ocultar detalle' : 'Ver detalle de la devolución'}>
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label={
                expanded ? 'Ocultar detalle de la devolución' : 'Ver detalle de la devolución'
              }
              aria-expanded={expanded}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5, pl: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'error.main', display: 'block', mb: 0.75 }}
          >
            Piezas devueltas ({entry.items.length})
          </Typography>
          {entry.items.map((item) => (
            <ReturnProductLine key={item.id} item={item} formatCurrency={formatCurrency} />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total reembolsado:{' '}
              <Typography component="span" variant="caption" fontWeight={700} color="error.main">
                {formatCurrency(refundTotal)}
              </Typography>
            </Typography>
          </Box>
          {notesMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
            >
              {notesMessage}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const ExchangeHistoryRow: React.FC<{
  entry: Extract<HistoryTimelineEntry, { kind: 'exchange' }>;
  expanded: boolean;
  onToggle: () => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
  isLast: boolean;
}> = ({ entry, expanded, onToggle, formatCurrency, formatDate, isLast }) => {
  const outTotal = entry.outItems.reduce((sum, i) => sum + toMoney(i.finalPrice), 0);
  const inTotal = entry.inItems.reduce((sum, i) => sum + toMoney(i.finalPrice), 0);
  const summary = getExchangeSummaryLabel(entry.outItems.length, entry.inItems.length);
  const notesMessage = formatNotesMessage(entry.notes);
  const rejectedByRequest = entry.rejectedByRequest;
  const chipLabel = rejectedByRequest ? 'Intercambio con solicitud rechazada' : 'Intercambio';
  const chipStyleKey = rejectedByRequest ? 'EXCHANGE_REJECTED' : 'EXCHANGE';

  return (
    <Box sx={rowShellSx(isLast)}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<SwapHorizIcon sx={{ fontSize: '14px !important' }} />}
              label={chipLabel}
              size="small"
              sx={(theme) => ({
                fontWeight: 600,
                ...getActionChipStyles(chipStyleKey, theme),
                border: 'none',
                '& .MuiChip-icon': { color: 'inherit' },
              })}
            />
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDate(entry.createdAt)} · {entry.processedByUsername}
          </Typography>
          {!expanded && notesMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
              noWrap
            >
              {notesMessage}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
          {entry.outItems.length > 0 && (
            <ExchangeAmountSummary
              label="Sale"
              amount={formatCurrency(outTotal)}
              icon={<RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />}
              color="error.main"
            />
          )}
          {entry.inItems.length > 0 && (
            <ExchangeAmountSummary
              label="Ingresa"
              amount={formatCurrency(inTotal)}
              icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
              color="success.main"
            />
          )}
          <Tooltip title={expanded ? 'Ocultar detalle' : 'Ver detalle del intercambio'}>
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label={
                expanded ? 'Ocultar detalle del intercambio' : 'Ver detalle del intercambio'
              }
              aria-expanded={expanded}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5, pl: 0.5 }}>
          {entry.outItems.length > 0 && (
            <Box sx={{ mb: entry.inItems.length > 0 ? 1.5 : 0 }}>
              <ExchangeBlockTitle
                icon={<RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label="Piezas a intercambiar"
                count={entry.outItems.length}
                color="error.main"
              />
              {entry.outItems.map((item) => (
                <ProductLine
                  key={item.id}
                  item={item}
                  formatCurrency={formatCurrency}
                  priceColor="error.main"
                />
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total que sale:{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                  >
                    {formatCurrency(outTotal)}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          )}

          {entry.inItems.length > 0 && (
            <Box>
              <ExchangeBlockTitle
                icon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                label="Piezas nuevas en la orden"
                count={entry.inItems.length}
                color="success.main"
              />
              {entry.inItems.map((item) => (
                <ProductLine
                  key={item.id}
                  item={item}
                  formatCurrency={formatCurrency}
                  priceColor="success.main"
                />
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total que ingresa:{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight={700}
                    color="success.main"
                  >
                    {formatCurrency(inTotal)}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          )}

          {notesMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
            >
              {notesMessage}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export const ProductHistorySection: React.FC<ProductHistorySectionProps> = ({
  orderId,
  formatCurrency,
  formatDate,
  embeddedHistory,
  sectionTitle = 'Historial de piezas',
  skipPermissionCheck = false,
}) => {
  const canViewRowFromPerm = usePermission(ORDERS_PERMISSIONS.VIEW_ROW);
  const canViewRow = skipPermissionCheck || canViewRowFromPerm;
  const [history, setHistory] = useState<ProductHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedExchanges, setExpandedExchanges] = useState<Set<string>>(new Set());
  const [expandedReturns, setExpandedReturns] = useState<Set<string>>(new Set());

  const toggleExchange = useCallback((exchangeId: string) => {
    setExpandedExchanges((prev) => {
      const next = new Set(prev);
      if (next.has(exchangeId)) {
        next.delete(exchangeId);
      } else {
        next.add(exchangeId);
      }
      return next;
    });
  }, []);

  const toggleReturn = useCallback((returnId: string) => {
    setExpandedReturns((prev) => {
      const next = new Set(prev);
      if (next.has(returnId)) {
        next.delete(returnId);
      } else {
        next.add(returnId);
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!canViewRow) {
      setHistory([]);
      setError(null);
      setIsLoading(false);
      setExpandedExchanges(new Set());
      return;
    }

    if (embeddedHistory != null) {
      setHistory(embeddedHistory);
      setError(null);
      setIsLoading(false);
      setExpandedExchanges(new Set());
      return;
    }

    if (!orderId) {
      setHistory([]);
      return;
    }

    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { salesService } = await import('../../../../services/sales.service');
        const data = await salesService.getOrderProductHistory(orderId);
        setHistory((data as ProductHistoryItem[]) || []);
        setExpandedExchanges(new Set());
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Error al cargar el historial';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadHistory();
  }, [orderId, canViewRow, embeddedHistory]);

  const timeline = useMemo(() => buildProductHistoryTimeline(history), [history]);

  const sectionHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <S.SectionAvatar>
        <HistoryIcon sx={{ fontSize: 16 }} />
      </S.SectionAvatar>
      <Typography
        variant="subtitle1"
        sx={(theme) => ({
          fontWeight: 700,
          color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.dark,
        })}
      >
        {sectionTitle}
        {timeline.length > 0 ? ` (${timeline.length})` : ''}
      </Typography>
    </Box>
  );

  if (!canViewRow) {
    return null;
  }

  if (isLoading) {
    return (
      <S.SectionBox>
        <S.SectionHeader>{sectionHeader}</S.SectionHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </S.SectionBox>
    );
  }

  if (error) {
    return (
      <S.SectionBox>
        <S.SectionHeader>{sectionHeader}</S.SectionHeader>
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </S.SectionBox>
    );
  }

  if (timeline.length === 0) {
    return null;
  }

  return (
    <S.SectionBox sx={{ mt: 2 }}>
      <S.SectionHeader>{sectionHeader}</S.SectionHeader>

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {timeline.map((entry, index) => {
          const isLast = index === timeline.length - 1;

          if (entry.kind === 'exchange') {
            return (
              <ExchangeHistoryRow
                key={entry.exchangeId}
                entry={entry}
                expanded={expandedExchanges.has(entry.exchangeId)}
                onToggle={() => toggleExchange(entry.exchangeId)}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                isLast={isLast}
              />
            );
          }

          if (entry.kind === 'return') {
            return (
              <ReturnHistoryRow
                key={entry.returnId}
                entry={entry}
                expanded={expandedReturns.has(entry.returnId)}
                onToggle={() => toggleReturn(entry.returnId)}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                isLast={isLast}
              />
            );
          }

          return (
            <SingleHistoryRow
              key={entry.item.id}
              item={entry.item}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              isLast={isLast}
            />
          );
        })}
      </Box>
    </S.SectionBox>
  );
};
